import isEqual from "lodash/isEqual";

import type { VariableSet } from "@/components/Playground/types";
import { getVariableValueIsFilled, parseTypeOptimistically } from "@/components/Playground/utils";
import type {
  PlaygroundEvaluateColumn,
  PlaygroundEvaluateRunDetail,
  PlaygroundEvaluateRunStatus,
} from "@/queries/playground/evaluate";
import type {
  CriterionSummary,
  EvaluatorResult,
  ScorecardAssertionSummary,
  GetScorecardRowResponse,
  ScorecardRowSummary,
  ScorecardStep,
} from "@/queries/smart-tables/scorecard";
import {
  CellStatus,
  ColumnType,
  type GridRow,
  type SmartCell,
  type SmartColumn,
} from "@/queries/smart-tables/types";
import type {
  ScorecardAllRowsListState,
  ScorecardCriterionRowsListState,
  ScorecardViewState,
} from "@/routes/smart-table/components/SmartTablePage/RightPanel/ScoreSidebarContent/Scorecard/types";
import {
  formatExecutionMetadataLatency,
  formatExecutionMetadataPrice,
} from "@/routes/smart-table/utils/executionMetadataFormatting";
import { resolveCellDisplayState } from "@/routes/smart-table/utils/cellDisplayState";

export const PROMPT_TEMPLATE_COLUMN_TYPE = "PROMPT_TEMPLATE";

export const isEvaluateInputColumn = (column: PlaygroundEvaluateColumn): boolean =>
  column.type === ColumnType.TEXT;

export const normalizeCriterionSummaries = (value: unknown): CriterionSummary[] => {
  let summaries: unknown[] = [];
  if (Array.isArray(value)) {
    summaries = value;
  } else if (typeof value === "object" && value !== null) {
    summaries = Object.values(value);
  }

  return summaries.filter(
    (summary): summary is CriterionSummary =>
      typeof summary === "object" &&
      summary !== null &&
      "step_id" in summary &&
      typeof summary.step_id === "string",
  );
};

export const EXECUTION_METADATA_COLUMN_TYPES = new Set<ColumnType>([
  ColumnType.PROMPT_TEMPLATE,
  ColumnType.WORKFLOW,
]);
const EXECUTION_METADATA_METRIC_KEYS = new Set(["price", "latency_ms"]);

export const EVALUATE_GRID_EDITING = {
  enabled: false,
  readOnly: true,
  singleClick: true,
} as const;
export const EVALUATE_GRID_SELECTION = { cellSelection: true, enableTextSelection: false } as const;
export const EVALUATE_GRID_DISPLAY = {
  enableSorting: false,
  enableFiltering: false,
  enableResizing: true,
  enableCellChangeFlash: false,
} as const;
export const EVALUATE_GRID_ROW_NUMBERS = {
  suppressCellSelectionIntegration: true,
  width: 60,
  minWidth: 60,
} as const;
export const EVALUATE_GRID_OPTIONS = {
  domLayout: "autoHeight",
  animateRows: false,
  suppressMovableColumns: true,
  cellFlashDuration: 0,
  cellFadeDuration: 0,
} as const;

export interface EvaluateAdHocRow {
  id: string;
  _rowIndex: number;
  _variableSetId?: string;
  _variableSetName?: string;
  _pendingHydration?: boolean;
  [key: string]: unknown;
}

export const scoreLabel = (score: number | null | undefined) =>
  typeof score === "number" ? `${Math.round(score * 100)}%` : "—";

export const verdictClassName = (verdict?: string | null) => {
  if (verdict === "pass") {
    return "border-green-200 bg-green-50 text-green-700";
  }
  if (verdict === "warn") {
    return "border-amber-200 bg-amber-50 text-amber-700";
  }
  if (verdict === "fail" || verdict === "error") {
    return "border-red-200 bg-red-50 text-red-700";
  }
  return "border-gray-200 bg-gray-50 text-gray-600";
};

export const getVariableNames = (
  playground: {
    detectedInputVariables: string[];
    input_variables: { key: string }[];
    input_variable_sets?: { variables: { key: string }[] }[];
  },
  additionalVariableNames: string[] = [],
) =>
  Array.from(
    new Set([
      ...playground.detectedInputVariables,
      ...playground.input_variables.map(variable => variable.key).filter(Boolean),
      ...(playground.input_variable_sets ?? []).flatMap(variableSet =>
        variableSet.variables.map(variable => variable.key).filter(Boolean),
      ),
      ...additionalVariableNames.filter(Boolean),
    ]),
  );

export const toSmartColumn = (column: PlaygroundEvaluateColumn): SmartColumn => ({
  id: column.id,
  title: column.title,
  position_rank: 0,
  type: Object.values(ColumnType).includes(column.type as ColumnType)
    ? (column.type as ColumnType)
    : ColumnType.TEXT,
  config: {},
  config_hash: "",
  is_output_column: column.is_output_column,
  dependencies: [],
});

export const getSinglePromptTemplateOverride = (columns: PlaygroundEvaluateColumn[]) => {
  const promptColumns = columns.filter(column => column.type === PROMPT_TEMPLATE_COLUMN_TYPE);
  return promptColumns.length === 1 ? { column_id: promptColumns[0].id } : undefined;
};

export const isExecutionMetadataMetricKey = (value: unknown): value is "price" | "latency_ms" =>
  typeof value === "string" && EXECUTION_METADATA_METRIC_KEYS.has(value);

export const isExecutionMetadataChildColumn = (column: SmartColumn | undefined): boolean =>
  Boolean(
    column &&
      isExecutionMetadataMetricKey(column.metric_key) &&
      (column.group_parent_column_id || column.config?.composition_kind === "execution_metadata"),
  );

export const executionMetadataRootHeader = (column: SmartColumn): string =>
  column.type === ColumnType.PROMPT_TEMPLATE ? "Prompt" : "Workflow";

export const executionMetadataChildHeader = (parent: SmartColumn, child: SmartColumn): string => {
  const label = child.metric_label?.trim();
  if (label) {
    return `${label.charAt(0).toUpperCase()}${label.slice(1)}`;
  }

  const title = child.title.startsWith(`${parent.title}.`)
    ? child.title.slice(parent.title.length + 1)
    : child.title;
  return `${title.charAt(0).toUpperCase()}${title.slice(1)}`;
};

export const executionMetadataValueFormatter = (metricKey: unknown) => {
  if (metricKey === "price") {
    return ({ value }: { value: unknown }) => formatExecutionMetadataPrice(value);
  }
  if (metricKey === "latency_ms") {
    return ({ value }: { value: unknown }) => formatExecutionMetadataLatency(value);
  }
  return undefined;
};

const toRecord = (value: unknown): Record<string, unknown> | null =>
  typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

const toFiniteNumber = (value: unknown): number | null => {
  const number = typeof value === "string" && value.trim() ? Number(value) : value;
  return typeof number === "number" && Number.isFinite(number) ? number : null;
};

export const getEvaluateExecutionMetric = (
  runRow: PlaygroundEvaluateRunDetail["rows"][number] | undefined,
  metricKey: "price" | "latency_ms",
): number | null => {
  const output = toRecord(runRow?.output_payload);
  const display = toRecord(output?.display_value);
  const rawDisplay = toRecord(toRecord(toRecord(output?.raw)?.display_value)?.value);
  const source = display ?? rawDisplay;

  if (metricKey === "price") {
    return toFiniteNumber(source?.total_price);
  }

  const latencySeconds = toFiniteNumber(source?.latency);
  return latencySeconds === null ? null : latencySeconds * 1_000;
};

const toCellStatus = (status: PlaygroundEvaluateRunStatus | undefined): CellStatus => {
  switch (status) {
    case "queued":
      return CellStatus.QUEUED;
    case "running":
      return CellStatus.RUNNING;
    case "completed":
      return CellStatus.COMPLETED;
    case "failed":
      return CellStatus.FAILED;
    case "cancelled":
    default:
      return CellStatus.STALE;
  }
};

const toErrorEnvelope = (error: unknown): Record<string, unknown> | null => {
  if (typeof error === "string" && error.trim()) {
    return { error: { message: error } };
  }
  if (typeof error === "object" && error !== null) {
    const errorRecord = error as Record<string, unknown>;
    return "error" in errorRecord ? errorRecord : { error: errorRecord };
  }
  return null;
};

export const toCellDisplayText = (value: unknown): string => {
  if (value === null || value === undefined) {
    return "";
  }
  if (typeof value === "string") {
    return value;
  }
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

export const getPromptCellPayload = (
  runRow: PlaygroundEvaluateRunDetail["rows"][number] | undefined,
  fallbackStatus: PlaygroundEvaluateRunStatus | undefined,
  isStale = false,
) => {
  const outputPayload = runRow?.output_payload;
  const outputRecord =
    typeof outputPayload === "object" && outputPayload !== null
      ? (outputPayload as Record<string, unknown>)
      : null;
  const rawOutput =
    outputRecord && typeof outputRecord.raw === "object" && outputRecord.raw !== null
      ? (outputRecord.raw as Record<string, unknown>)
      : null;
  const rowError = toErrorEnvelope(runRow?.error);
  const payloadError = outputRecord && "error" in outputRecord ? outputRecord : null;

  let status = toCellStatus(runRow?.status ?? fallbackStatus);
  if (
    isStale &&
    status !== CellStatus.QUEUED &&
    status !== CellStatus.DISPATCHED &&
    status !== CellStatus.RUNNING
  ) {
    status = CellStatus.STALE;
  }

  let rawValue: unknown = rowError ?? payloadError;
  let displayValue: unknown = null;

  if (!rawValue && outputPayload !== null && outputPayload !== undefined) {
    const verboseDisplay = toRecord(outputRecord?.display_value);
    const rawDisplay = toRecord(rawOutput?.display_value);
    const rawVerboseDisplay = toRecord(rawDisplay?.value);
    const responseValue =
      outputRecord?.value ??
      verboseDisplay?.response ??
      rawVerboseDisplay?.response ??
      outputPayload;

    rawValue = rawOutput?.value ?? responseValue;
    displayValue = toCellDisplayText(responseValue);
    if (
      rawOutput?.value === undefined &&
      runRow?.request_id !== null &&
      runRow?.request_id !== undefined
    ) {
      rawValue = { value: responseValue, request_id: runRow.request_id };
    }
  }

  let displayState = resolveCellDisplayState({
    status,
    display_value: displayValue,
    value: rawValue,
  });
  if (
    displayState.hasErrorValue &&
    status !== CellStatus.STALE &&
    status !== CellStatus.QUEUED &&
    status !== CellStatus.DISPATCHED &&
    status !== CellStatus.RUNNING
  ) {
    status = CellStatus.FAILED;
    displayState = resolveCellDisplayState({
      status,
      display_value: displayValue,
      value: rawValue,
    });
  }

  return { displayState, displayValue, rawValue, status };
};

export const getScoringErrorSummary = (
  scorePayload: Record<string, unknown> | null,
): string | null => {
  if (scorePayload?.status !== "failed") {
    return null;
  }

  const { message } = scorePayload;
  return typeof message === "string" && message.trim() ? message : null;
};

export const normalizeEvaluatorResults = (
  breakdown?: Record<string, Record<string, unknown>> | null,
): Record<string, EvaluatorResult> =>
  Object.fromEntries(
    Object.entries(breakdown || {}).map(([stepId, result]) => {
      let errorMessage: string | undefined;
      if (typeof result.error_message === "string") {
        errorMessage = result.error_message;
      } else if (typeof result.message === "string") {
        errorMessage = result.message;
      }
      return [
        stepId,
        {
          score: typeof result.score === "number" ? result.score : null,
          verdict: typeof result.verdict === "string" ? result.verdict : "skipped",
          evidence: typeof result.evidence === "string" ? result.evidence : undefined,
          raw_value: result.raw_value ?? result.output ?? null,
          aggregate_result:
            typeof result.aggregate_result === "object" && result.aggregate_result !== null
              ? (result.aggregate_result as Record<string, unknown>)
              : undefined,
          error_message: errorMessage,
          config_hash: typeof result.config_hash === "string" ? result.config_hash : undefined,
          input_hash: typeof result.input_hash === "string" ? result.input_hash : undefined,
        },
      ];
    }),
  );

const getConfiguredAssertionTexts = (step: Pick<ScorecardStep, "primitive_config">): string[] => {
  const { prompt } = step.primitive_config;
  if (typeof prompt !== "string" || !prompt.trim()) {
    return [];
  }

  try {
    const parsed = JSON.parse(prompt);
    if (Array.isArray(parsed)) {
      return parsed.map(assertion => String(assertion)).filter(Boolean);
    }
  } catch {
    // A non-JSON prompt is a single assertion.
  }
  return [prompt];
};

export const buildAssertionSummariesFromRunRows = (
  rows: PlaygroundEvaluateRunDetail["rows"],
  step: Pick<ScorecardStep, "id" | "primitive_config">,
): ScorecardAssertionSummary[] => {
  const assertionCounts = new Map<string, { passCount: number; failCount: number }>();
  const configuredAssertions = getConfiguredAssertionTexts(step);

  const recordOutcome = (assertion: string, passed: boolean) => {
    const counts = assertionCounts.get(assertion) ?? { passCount: 0, failCount: 0 };
    if (passed) {
      counts.passCount += 1;
    } else {
      counts.failCount += 1;
    }
    assertionCounts.set(assertion, counts);
  };

  rows.forEach(row => {
    const rawValue = normalizeEvaluatorResults(row.evaluator_breakdown)[step.id]?.raw_value;
    if (typeof rawValue === "boolean") {
      if (configuredAssertions.length === 1) {
        recordOutcome(configuredAssertions[0], rawValue);
      }
      return;
    }
    if (!rawValue || typeof rawValue !== "object" || Array.isArray(rawValue)) {
      return;
    }

    Object.entries(rawValue).forEach(([assertion, result]) => {
      let passed: boolean | null = null;
      if (typeof result === "boolean") {
        passed = result;
      } else if (result && typeof result === "object" && !Array.isArray(result)) {
        const { value } = result as Record<string, unknown>;
        passed = typeof value === "boolean" ? value : null;
      }
      if (passed === null) {
        return;
      }

      recordOutcome(assertion, passed);
    });
  });

  return Array.from(assertionCounts, ([assertion, counts]) => ({
    assertion,
    pass_count: counts.passCount,
    fail_count: counts.failCount,
    total_count: counts.passCount + counts.failCount,
  }));
};

export const buildCriterionSummaries = (
  steps: ScorecardStep[],
  rowBreakdowns: Record<number, GetScorecardRowResponse>,
): CriterionSummary[] =>
  steps.map(step => {
    const stepResults = Object.values(rowBreakdowns)
      .map(row => row.step_results[step.id])
      .filter(Boolean);
    const scores = stepResults
      .map(result => result.score)
      .filter((score): score is number => typeof score === "number");
    const passCount = stepResults.filter(result => result.verdict === "pass").length;
    const warnCount = stepResults.filter(result => result.verdict === "warn").length;
    const failCount = stepResults.filter(result => result.verdict === "fail").length;
    const errorCount = stepResults.filter(result => result.verdict === "error").length;
    const skippedCount = stepResults.filter(result => result.verdict === "skipped").length;
    const aggregateResult = stepResults.find(result => result.aggregate_result)?.aggregate_result;
    let verdict = "skipped";
    if (errorCount > 0) {
      verdict = "error";
    } else if (failCount > 0) {
      verdict = "fail";
    } else if (warnCount > 0) {
      verdict = "warn";
    } else if (passCount > 0) {
      verdict = "pass";
    }
    return {
      step_id: step.id,
      title: step.title,
      score: scores.length ? scores.reduce((sum, score) => sum + score, 0) / scores.length : null,
      aggregate_result: aggregateResult,
      verdict,
      completed_row_count: stepResults.length - skippedCount - errorCount,
      pass_row_count: passCount,
      warn_row_count: warnCount,
      fail_row_count: failCount,
      skipped_row_count: skippedCount,
      error_row_count: errorCount,
    };
  });

export const countVerdicts = (values: string[]) => ({
  all: values.length,
  pass: values.filter(value => value === "pass").length,
  warn: values.filter(value => value === "warn").length,
  fail: values.filter(value => value === "fail").length,
  error: values.filter(value => value === "error").length,
});

export const filterRowsForScorecardView = ({
  allRowsListState,
  criterionRowsListState,
  rowBreakdowns,
  rowSummaries,
  view,
}: {
  allRowsListState: ScorecardAllRowsListState;
  criterionRowsListState: ScorecardCriterionRowsListState;
  rowBreakdowns: Record<number, GetScorecardRowResponse>;
  rowSummaries: ScorecardRowSummary[];
  view: ScorecardViewState;
}) => {
  if (view.view === "criterion") {
    if (criterionRowsListState.filter === "all") {
      return rowSummaries;
    }
    return rowSummaries.filter(
      row =>
        rowBreakdowns[row.row_index]?.step_results[view.stepId]?.verdict ===
        criterionRowsListState.filter,
    );
  }
  if (view.view === "all-rows" && allRowsListState.filter !== "all") {
    return rowSummaries.filter(row => row.aggregate_verdict === allRowsListState.filter);
  }
  return rowSummaries;
};

export const getSourceSheetCellValue = (cell: SmartCell | undefined): unknown => {
  if (!cell) {
    return undefined;
  }

  const rawValue = cell.value;
  if (
    typeof rawValue === "object" &&
    rawValue !== null &&
    !Array.isArray(rawValue) &&
    Object.keys(rawValue).length === 1 &&
    "value" in rawValue
  ) {
    return parseTypeOptimistically(rawValue.value);
  }
  if (rawValue !== null && rawValue !== undefined) {
    return parseTypeOptimistically(rawValue);
  }
  return cell.display_value === null ? undefined : parseTypeOptimistically(cell.display_value);
};

export const getEffectiveInputVariableSets = ({
  variableSets,
  inputMappings,
  sourceRows,
  requiredVariableNames = [],
}: {
  variableSets: Pick<VariableSet, "variables">[];
  inputMappings: Record<string, { column_id: string }>;
  sourceRows: GridRow[];
  requiredVariableNames?: string[];
}): Record<string, unknown>[] =>
  variableSets.map((variableSet, rowIndex) => {
    const sourceRow = sourceRows[rowIndex];
    const variableNames = new Set([
      ...requiredVariableNames,
      ...variableSet.variables.map(variable => variable.key),
      ...Object.keys(inputMappings),
    ]);

    return Object.fromEntries(
      Array.from(variableNames).map(variableName => {
        const variable = variableSet.variables.find(candidate => candidate.key === variableName);
        if (variable && getVariableValueIsFilled(variable.value)) {
          return [variableName, parseTypeOptimistically(variable.value)];
        }

        const mappedColumnId = inputMappings[variableName]?.column_id;
        const sourceValue = mappedColumnId
          ? getSourceSheetCellValue(sourceRow?.cells[mappedColumnId])
          : undefined;
        return [
          variableName,
          sourceValue === undefined ? parseTypeOptimistically(variable?.value ?? "") : sourceValue,
        ];
      }),
    );
  });

export const getMappedInputVariableValue = (
  runRow: PlaygroundEvaluateRunDetail["rows"][number] | undefined,
  variableName: string | undefined,
  variableSet: { variables: { key: string; value: unknown }[] } | undefined,
  sourceValue?: unknown,
) => {
  if (!variableName) {
    return null;
  }

  const draftVariable = variableSet?.variables.find(variable => variable.key === variableName);
  if (draftVariable && getVariableValueIsFilled(draftVariable.value)) {
    return parseTypeOptimistically(draftVariable.value);
  }
  if (sourceValue !== undefined) {
    return sourceValue;
  }

  return runRow && variableName in runRow.input_variables
    ? runRow.input_variables[variableName]
    : "";
};

const normalizeInputMappings = (
  mappings: Record<string, { column_id: string }> | undefined,
): Record<string, string> =>
  Object.fromEntries(
    Object.entries(mappings ?? {}).map(([variable, mapping]) => [variable, mapping.column_id]),
  );

export interface EvaluateRunStaleState {
  isRunStale: boolean;
  isPromptOutputStale: boolean;
  staleStepIds: string[];
}

const freshEvaluateRunState = (): EvaluateRunStaleState => ({
  isRunStale: false,
  isPromptOutputStale: false,
  staleStepIds: [],
});

export const getEvaluateRunStaleState = ({
  run,
  currentInputVariableSets,
  inputMappings,
  templateOverrideColumnId,
}: {
  run: PlaygroundEvaluateRunDetail | null | undefined;
  currentInputVariableSets: Record<string, unknown>[];
  inputMappings: Record<string, { column_id: string }>;
  templateOverrideColumnId?: string;
}): EvaluateRunStaleState => {
  if (!run || run.status === "queued" || run.status === "running") {
    return freshEvaluateRunState();
  }

  const allPreviousMappings = normalizeInputMappings(run.input_variable_mappings_snapshot);
  const allCurrentMappings = normalizeInputMappings(inputMappings);
  const hasMappingSnapshot = run.input_variable_mappings_snapshot !== undefined;
  const variableNames = new Set([
    ...currentInputVariableSets.flatMap(inputSet => Object.keys(inputSet)),
    ...run.rows.flatMap(row => Object.keys(row.input_variables)),
  ]);
  const previousMappings = Object.fromEntries(
    Object.entries(allPreviousMappings).filter(([variable]) => variableNames.has(variable)),
  );
  const currentMappings = Object.fromEntries(
    Object.entries(allCurrentMappings).filter(([variable]) => variableNames.has(variable)),
  );
  const affectedColumnIds = new Set<string>();
  const inputRowCountChanged = run.rows.length !== currentInputVariableSets.length;
  let inputValuesChanged = inputRowCountChanged;
  let mappingsChanged = false;

  variableNames.forEach(variable => {
    const previousColumnId = previousMappings[variable];
    const currentColumnId = currentMappings[variable];
    const comparedRowCount = Math.max(run.rows.length, currentInputVariableSets.length);
    const variableValueChanged = Array.from({ length: comparedRowCount }).some(
      (_, index) =>
        !isEqual(
          run.rows[index]?.input_variables[variable],
          currentInputVariableSets[index]?.[variable],
        ),
    );
    const variableMappingChanged = hasMappingSnapshot && previousColumnId !== currentColumnId;

    inputValuesChanged ||= variableValueChanged;
    mappingsChanged ||= variableMappingChanged;

    if (variableValueChanged || variableMappingChanged) {
      if (previousColumnId) {
        affectedColumnIds.add(previousColumnId);
      }
      if (currentColumnId) {
        affectedColumnIds.add(currentColumnId);
      }
    }
  });

  const previousTemplateColumnId = run.template_override_snapshot?.column_id;
  const templateOverrideChanged = run.template_override_snapshot
    ? previousTemplateColumnId !== templateOverrideColumnId
    : false;
  const isPromptOutputStale = inputValuesChanged || templateOverrideChanged;

  if (isPromptOutputStale) {
    if (previousTemplateColumnId) {
      affectedColumnIds.add(previousTemplateColumnId);
    }
    if (templateOverrideColumnId) {
      affectedColumnIds.add(templateOverrideColumnId);
    }
  }

  const failedStepIds = new Set(
    normalizeCriterionSummaries(run.summary?.criterion_summaries)
      .filter(summary => summary.verdict === "fail" || summary.verdict === "error")
      .map(summary => summary.step_id),
  );
  run.rows.forEach(row => {
    Object.entries(row.evaluator_breakdown ?? {}).forEach(([stepId, result]) => {
      if (result.verdict === "fail" || result.verdict === "error") {
        failedStepIds.add(stepId);
      }
    });
  });

  const staleStepIds = (run.scorecard_snapshot?.steps ?? [])
    .filter(
      step =>
        inputRowCountChanged ||
        (!failedStepIds.has(step.id) &&
          step.source_column_ids.some(columnId => affectedColumnIds.has(columnId))),
    )
    .map(step => step.id);

  return {
    isRunStale: inputValuesChanged || mappingsChanged || templateOverrideChanged,
    isPromptOutputStale,
    staleStepIds,
  };
};
